import { useEffect, useState } from 'preact/hooks';
import { supabase } from './supabase';

/** Is the signed-in person the admin of this project? Each person can only see their own row. */
export function useIsAdmin(userId: string): boolean {
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    let live = true;
    supabase
      ?.from('app_admins')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (live) setAdmin(Boolean(data));
      });
    return () => {
      live = false;
    };
  }, [userId]);
  return admin;
}

/** Am I the admin? Each person sees only their own app_admins row, so any row means yes. */
export function useAmAdmin(): boolean {
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    let live = true;
    supabase
      ?.from('app_admins')
      .select('user_id')
      .limit(1)
      .then(({ data }) => {
        if (live) setAdmin(Boolean(data?.length));
      });
    return () => {
      live = false;
    };
  }, []);
  return admin;
}

export type FlagState = 'off' | 'chosen' | 'everyone';

export interface Flag {
  key: string;
  description: string;
  state: FlagState;
  version: number;
  /** Is the admin themself one of the chosen people? */
  me: boolean;
  /** How many other chosen people. */
  others: number;
}

export async function loadFlags(userId: string): Promise<Flag[]> {
  if (!supabase) return [];
  const [flags, people] = await Promise.all([
    supabase.from('feature_flags').select('key, description, state, version').order('key'),
    supabase.from('feature_flag_people').select('flag_key, user_id'),
  ]);
  if (flags.error) throw flags.error;
  if (people.error) throw people.error;
  return (flags.data ?? []).map((f) => {
    const rows = (people.data ?? []).filter((p) => p.flag_key === f.key);
    return {
      key: f.key,
      description: f.description,
      state: f.state as FlagState,
      version: f.version,
      me: rows.some((p) => p.user_id === userId),
      others: rows.filter((p) => p.user_id !== userId).length,
    };
  });
}

export async function setFlagState(key: string, state: FlagState): Promise<void> {
  const { error } = await supabase!.from('feature_flags').update({ state }).eq('key', key);
  if (error) throw error;
}

export async function setMeChosen(key: string, userId: string, on: boolean): Promise<void> {
  const res = on
    ? await supabase!.from('feature_flag_people').insert({ flag_key: key, user_id: userId })
    : await supabase!.from('feature_flag_people').delete().eq('flag_key', key).eq('user_id', userId);
  if (res.error) throw res.error;
}
